// import { useState, useEffect } from "react";
import React, { useState, useEffect } from "react";
import {
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    CircularProgress,
    Typography,
    Tooltip,
    InputAdornment,
    TableContainer,
    Fade,
    Chip,
    IconButton,
    Drawer,
    useMediaQuery,
    useTheme,
    Collapse
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { fetchData } from "../../../apiService";
import { useAuth } from "../../../AuthContext";
import { tokenIsExpired } from "../../../authUtils";

function DetailedInvoiceReport() {
    const { isAuthenticated, companyId, branchId, token, logout, username } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    
    // Initialize dates: 1st of current month and today
    const getFirstDayOfMonth = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
        const dateStr = `${year}-${month}-01`;
        console.log('getFirstDayOfMonth called, returning:', dateStr);
        return dateStr;
    };
    
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        console.log('getTodayDate called, returning:', dateStr);
        return dateStr;
    };
    
    const [fromDate, setFromDate] = useState(getFirstDayOfMonth());
    const [toDate, setToDate] = useState(getTodayDate());
    const [isLoading, setIsLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [statusFilter, setStatusFilter] = useState("All");
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [companyDetails, setCompanyDetails] = useState(null);
    const [branchDetails, setBranchDetails] = useState(null);

    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');

    // useEffect(() => {
    //     setMounted(true);
    //     if (isAuthenticated && tokenIsExpired(token)) {
    //         logout();
    //         return;
    //     }

    //     const today = new Date();
    //     const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    //     setFromDate(firstDayOfMonth.toISOString().split("T")[0]);
    //     setToDate(today.toISOString().split("T")[0]);
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []);

    // useEffect(() => {
    //     if (fromDate && toDate && companyId && branchId && token) {
    //         fetchTableData();
    //         fetchCompanyDetails();
    //         fetchBranchDetails();
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [fromDate, toDate]);
    useEffect(() => {
        setMounted(true);
        
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, []);

    useEffect(() => {

        if (fromDate && toDate && companyId && branchId && token) {
            fetchTableData();
            fetchCompanyDetails();
            fetchBranchDetails();
        }

    }, [fromDate, toDate]);


    const fetchCompanyDetails = async () => {
        try {
            const response = await fetchData('CompanyApi', `/${companyId}`, token);
            if (response && response.responseCode === '1111') {
                const data = response.response_data || response.data || response;
                setCompanyDetails(Array.isArray(data) ? data[0] : data);
            }
        } catch (error) {
            console.error('Error fetching company details:', error);
        }
    };

    const fetchBranchDetails = async () => {
        try {
            const response = await fetchData('BranchApi', `/${branchId}`, token);
            if (response && response.responseCode === '1111') {
                const data = response.response_data || response.data || response;
                setBranchDetails(Array.isArray(data) ? data[0] : data);
            }
        } catch (error) {
            console.error('Error fetching branch details:', error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        // If date is already in DD/MM/YYYY format, return as is
        if (dateString.includes('/')) return dateString;
        // Otherwise, format it
        return new Date(dateString).toLocaleDateString("en-GB");
    };


    const fetchTableData = async () => {
        setDataItem([]);

        // Delay showing loading overlay to prevent flash on quick loads
        const loadingTimeout = setTimeout(() => {
            setLoading(true);
            setLoadingMessage('Loading cash book data...');
        }, 300);

        try {
            const response = await fetchData('ReportApi', `/DetailedInvoiceData/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setDataItem(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Packing Slip Details:', error);
        } finally {
            clearTimeout(loadingTimeout);
            setLoading(false);
        }
    };

    const toggleRow = (BillNo) => {
        setExpandedRows(prev => ({
            ...prev,
            [BillNo]: !prev[BillNo]
        }));
    };

    const filteredData = dataItem
        .filter((item) => {
            if (statusFilter === "Completed") {
                return item.Status === "Delivery In Completed";
            } else if (statusFilter === "Pending") {
                return item.Status !== "Delivery In Completed";
            }
            return true;
        })
        .filter((item) =>
            Object.values(item).some((value) =>
                String(value).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );

    const processedData = filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );


    // const handleExportExcel = async () => {

    //     if (groupedData.length === 0) {
    //         alert("No data to export");
    //         return;
    //     }

    //     const workbook = new ExcelJS.Workbook();
    //     const worksheet = workbook.addWorksheet("Detailed Invoice");

    //     // ===== REPORT TITLE =====
    //     const titleRow = worksheet.addRow(["DETAILED INVOICE REPORT"]);
    //     titleRow.font = { bold: true, size: 16 };
    //     titleRow.alignment = { horizontal: "center" };
    //     worksheet.mergeCells("A1:N1");

    //     worksheet.addRow([]);

    //     // ===== COMPANY DETAILS =====
    //     const companyName = companyDetails?.CompanyName || companyDetails?.Name || "N/A";
    //     const branchName = branchDetails?.BranchName || branchDetails?.Name || "N/A";

    //     worksheet.addRow(["Company:", companyName, "", "Branch:", branchName]);

    //     worksheet.addRow([
    //         "Date Range:",
    //         `${formatDate(fromDate)} to ${formatDate(toDate)}`,
    //         "",
    //         "Generated By:",
    //         username || "N/A"
    //     ]);

    //     const currentDateTime = new Date().toLocaleString();

    //     worksheet.addRow(["Generated Time:", currentDateTime]);

    //     worksheet.addRow([]);

    //     // ===== COLUMN WIDTH =====
    //     worksheet.columns = [
    //         { width: 15 }, // Invoice
    //         { width: 15 }, // Date
    //         { width: 25 }, // Customer
    //         { width: 18 }, // Contact
    //         { width: 12 }, // Cash
    //         { width: 12 }, // UPI
    //         { width: 12 }, // Card
    //         { width: 12 }, // Total
    //         { width: 12 }, // Balance
    //         { width: 30 }, // Item
    //         { width: 10 }, // Qty
    //         { width: 12 }, // Rate
    //         { width: 12 }, // Amount
    //         { width: 12 }  // VAT
    //     ];

    //     // ===== DATA =====
    //     groupedData.forEach((invoice) => {

    //         // ===== INVOICE ROW =====
    //         const invoiceRow = worksheet.addRow([
    //             invoice.BillNo,
    //             formatDate(invoice.BillDate),
    //             invoice.CustomerName,
    //             invoice.ContactNumber1,
    //             invoice.CashAmount,
    //             invoice.UPIAmount,
    //             invoice.CardAmount,
    //             invoice.TotalAmount,
    //             invoice.BalanceAmount,
    //             "",
    //             "",
    //             "",
    //             "",
    //             ""
    //         ]);

    //         invoiceRow.font = { bold: true };

    //         // ===== ITEM HEADER =====
    //         const itemHeader = worksheet.addRow([
    //             "",
    //             "",
    //             "",
    //             "",
    //             "",
    //             "",
    //             "",
    //             "",
    //             "",
    //             "Item",
    //             "Qty",
    //             "Rate",
    //             "Amount",
    //             "VAT"
    //         ]);

    //         itemHeader.font = { bold: true };

    //         itemHeader.fill = {
    //             type: "pattern",
    //             pattern: "solid",
    //             fgColor: { argb: "FFD9D9D9" }
    //         };

    //         itemHeader.eachCell((cell) => {
    //             cell.border = {
    //                 top: { style: "thin" },
    //                 bottom: { style: "thin" },
    //                 left: { style: "thin" },
    //                 right: { style: "thin" }
    //             };
    //         });

    //         // ===== ITEMS =====
    //         invoice.items.forEach((item) => {

    //             const row = worksheet.addRow([
    //                 "",
    //                 "",
    //                 "",
    //                 "",
    //                 "",
    //                 "",
    //                 "",
    //                 "",
    //                 "",
    //                 item.ItemName,
    //                 item.Quantity,
    //                 item.Rate,
    //                 item.Amount,
    //                 item.VATAmount
    //             ]);

    //             row.eachCell((cell, colNumber) => {

    //                 cell.border = {
    //                     top: { style: "thin" },
    //                     bottom: { style: "thin" },
    //                     left: { style: "thin" },
    //                     right: { style: "thin" }
    //                 };

    //                 // AED Currency
    //                 if (colNumber === 12 || colNumber === 13 || colNumber === 14) {
    //                     cell.numFmt = '"AED" #,##0.00';
    //                 }

    //                 if (colNumber === 11) {
    //                     cell.alignment = { horizontal: "center" };
    //                 }

    //             });

    //         });

    //         worksheet.addRow([]);

    //     });

    //     // ===== DOWNLOAD FILE =====
    //     const buffer = await workbook.xlsx.writeBuffer();

    //     const fileName = `Detailed_Invoice_Report_${fromDate}_to_${toDate}.xlsx`;

    //     saveAs(
    //         new Blob([buffer], {
    //             type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    //         }),
    //         fileName
    //     );

    // };

    const handleExportExcel = async () => {

        if (!groupedData || groupedData.length === 0) {
            alert("No data to export");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Detailed Invoice");

        // ===== REPORT TITLE =====
        const titleRow = worksheet.addRow(["DETAILED INVOICE REPORT"]);
        titleRow.font = { bold: true, size: 16 };
        titleRow.alignment = { horizontal: "center" };
        worksheet.mergeCells("A1:N1");

        worksheet.addRow([]);

        // ===== COMPANY DETAILS =====
        const companyName = companyDetails?.CompanyName || companyDetails?.Name || "N/A";
        const branchName = branchDetails?.BranchName || branchDetails?.Name || "N/A";

        worksheet.addRow(["Company:", companyName, "", "Branch:", branchName]);

        worksheet.addRow([
            "Date Range:",
            `${formatDate(fromDate)} to ${formatDate(toDate)}`,
            "",
            "Generated By:",
            username || "N/A"
        ]);

        const currentDateTime = new Date().toLocaleString();
        worksheet.addRow(["Generated Time:", currentDateTime]);

        worksheet.addRow([]);

        // ===== HEADER ROW =====
        const headerRow = worksheet.addRow([
            "Invoice #",
            "Date",
            "Customer",
            "Contact",
            "Cash",
            "UPI",
            "Card",
            "Total",
            "Balance",
            "Item",
            "Qty",
            "Rate",
            "Amount",
            "VAT"
        ]);

        headerRow.font = { bold: true };

        headerRow.eachCell((cell) => {
            cell.border = {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" }
            };
            cell.alignment = { horizontal: "center" };
        });

        // ===== COLUMN WIDTH =====
        worksheet.columns = [
            { width: 15 },
            { width: 15 },
            { width: 25 },
            { width: 18 },
            { width: 12 },
            { width: 12 },
            { width: 12 },
            { width: 12 },
            { width: 12 },
            { width: 30 },
            { width: 10 },
            { width: 12 },
            { width: 12 },
            { width: 12 }
        ];

        // ===== DATA =====
        groupedData.forEach((invoice) => {

            // ===== INVOICE ROW =====
            const invoiceRow = worksheet.addRow([
                invoice.BillNo,
                formatDate(invoice.BillDate),
                invoice.CustomerName,
                invoice.ContactNumber1,
                invoice.CashAmount,
                invoice.UPIAmount,
                invoice.CardAmount,
                invoice.TotalAmount,
                invoice.BalanceAmount,
                "",
                "",
                "",
                "",
                ""
            ]);

            invoiceRow.font = { bold: true };

            // ===== ITEMS =====
            invoice.items.forEach((item) => {

                const row = worksheet.addRow([
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    item.ItemName,
                    item.Quantity,
                    item.Rate,
                    item.Amount,
                    item.VATAmount
                ]);

                row.eachCell((cell, colNumber) => {

                    cell.border = {
                        top: { style: "thin" },
                        bottom: { style: "thin" },
                        left: { style: "thin" },
                        right: { style: "thin" }
                    };

                    // AED Currency Format
                    if (colNumber === 12 || colNumber === 13 || colNumber === 14) {
                        cell.numFmt = '"" #,##0.00';
                    }

                    if (colNumber === 11) {
                        cell.alignment = { horizontal: "center" };
                    }

                });

            });

            worksheet.addRow([]);

        });

        // ===== DOWNLOAD =====
        const buffer = await workbook.xlsx.writeBuffer();

        const fileName = `Detailed_Invoice_Report_${fromDate}_to_${toDate}.xlsx`;

        saveAs(
            new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            }),
            fileName
        );

    };


    if (!mounted) return null;

    const groupedData = Object.values(
        processedData.reduce((acc, row) => {

            const key = `${row.BillNo}`;

            if (!acc[key]) {
                acc[key] = {
                    ...row,
                    items: []
                };
            }

            acc[key].items.push({
                ItemName: row.ItemName,
                Quantity: row.Quantity,
                Rate: row.Rate,
                Amount: row.Amount,
                VATAmount: row.VATAmount,
                TotalAmount: row.TotalAmount
            });

            return acc;

        }, {})
    );


    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '10px',
                position: 'relative'
            }}>
                <div style={{
                    width: '100%',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '12px',
                    position: 'relative',
                    color: 'black',
                    fontFamily: 'Times New Romans',
                }}>
                    <div style={{
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <h4 style={{
                            fontSize: '17px',
                            fontWeight: '700',
                            margin: 0,
                            letterSpacing: '-0.3px',
                            fontFamily: 'Times New Romans',
                            textTransform: 'uppercase',
                            color: 'black'
                        }}>
                            Detailed Invoice Report
                        </h4>
                    </div>

                    {!isMobile ? (
                        <div style={{
                            marginBottom: '12px',
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                        }}>
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                flexWrap: 'nowrap',
                                minWidth: 'fit-content',
                                alignItems: 'flex-end'
                            }}>
                                <div style={{ flexShrink: 0 }}>
                                    <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', paddingLeft: '4px' }}>From Date</div>
                                    <TextField
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        size="small"
                                        sx={{
                                            width: '140px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                backgroundColor: '#f8f9fa',
                                                fontSize: '11px',
                                                height: '28px',
                                                '&:hover fieldset': { borderColor: '#0f3460' },
                                                '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                                            },
                                        }}
                                    />
                                </div>

                                <div style={{ flexShrink: 0 }}>
                                    <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', paddingLeft: '4px' }}>To Date</div>
                                    <TextField
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        size="small"
                                        sx={{
                                            width: '140px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                backgroundColor: '#f8f9fa',
                                                fontSize: '11px',
                                                height: '28px',
                                                '&:hover fieldset': { borderColor: '#0f3460' },
                                                '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                                            },
                                        }}
                                    />
                                </div>

                                {/*<div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                     <Button
                                        variant={statusFilter === "All" ? "contained" : "outlined"}
                                        onClick={() => setStatusFilter("All")}
                                        style={{
                                            background: statusFilter === "All" ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' : 'transparent',
                                            color: statusFilter === "All" ? 'white' : '#0f3460',
                                            borderColor: '#0f3460',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            height: '28px',
                                            textTransform: 'none',
                                            minWidth: '60px'
                                        }}
                                    >
                                        All
                                    </Button>
                                    <Button
                                        variant={statusFilter === "Completed" ? "contained" : "outlined"}
                                        onClick={() => setStatusFilter("Completed")}
                                        style={{
                                            background: statusFilter === "Completed" ? 'linear-gradient(135deg, #10b981, #059669)' : 'transparent',
                                            color: statusFilter === "Completed" ? 'white' : '#10b981',
                                            borderColor: '#10b981',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            height: '28px',
                                            textTransform: 'none',
                                            minWidth: '90px'
                                        }}
                                    >
                                        Completed
                                    </Button>
                                    <Button
                                        variant={statusFilter === "Pending" ? "contained" : "outlined"}
                                        onClick={() => setStatusFilter("Pending")}
                                        style={{
                                            background: statusFilter === "Pending" ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'transparent',
                                            color: statusFilter === "Pending" ? 'white' : '#f59e0b',
                                            borderColor: '#f59e0b',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            height: '28px',
                                            textTransform: 'none',
                                            minWidth: '80px'
                                        }}
                                    >
                                        Pending
                                    </Button>
                                </div> */}

                                <TextField
                                    placeholder="Search deliveries..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    size="small"
                                    sx={{
                                        flexGrow: 1,
                                        minWidth: '200px',
                                        flexShrink: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '28px',
                                            '&:hover fieldset': { borderColor: '#0f3460' },
                                            '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon style={{ color: '#0f3460', fontSize: '14px' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    onClick={fetchTableData}
                                    disabled={isLoading}
                                    startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon style={{ fontSize: '14px' }} />}
                                    style={{
                                        background: 'linear-gradient(135deg, #11998e 0%, #072412ff 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        height: '28px',
                                        textTransform: 'none',
                                        flexShrink: 0
                                    }}
                                >
                                    {isLoading ? "Loading..." : "Refresh"}
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={handleExportExcel}
                                    disabled={filteredData.length === 0}
                                    startIcon={<FileDownloadIcon style={{ fontSize: '14px' }} />}
                                    style={{
                                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        height: '28px',
                                        textTransform: 'none',
                                        flexShrink: 0
                                    }}
                                >
                                    Export Excel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ marginBottom: '12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minWidth: 'fit-content' }}>
                                <TextField type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} size="small"
                                    sx={{ width: '130px', flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#f8f9fa', fontSize: '11px', height: '32px' } }} />
                                <TextField type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} size="small"
                                    sx={{ width: '130px', flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#f8f9fa', fontSize: '11px', height: '32px' } }} />
                                <Tooltip title="Refresh"><IconButton onClick={fetchTableData} disabled={isLoading}
                                    sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', width: '36px', height: '32px', borderRadius: '6px', flexShrink: 0 }}>
                                    {isLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}</IconButton></Tooltip>
                                <Tooltip title="Export"><IconButton onClick={handleExportExcel} disabled={filteredData.length === 0}
                                    sx={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', color: 'white', width: '36px', height: '32px', borderRadius: '6px', flexShrink: 0 }}>
                                    <FileDownloadIcon /></IconButton></Tooltip>
                                <Tooltip title="Filter & Search"><IconButton onClick={() => setFilterDrawerOpen(true)}
                                    sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', width: '36px', height: '32px', borderRadius: '6px', flexShrink: 0 }}>
                                    <FilterListIcon /></IconButton></Tooltip>
                            </div>
                        </div>
                    )}

                    <Drawer anchor="right" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
                        <div style={{ width: '280px', padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <Typography variant="h6" style={{ fontWeight: '600' }}>Filters</Typography>
                                <IconButton onClick={() => setFilterDrawerOpen(false)} size="small"><CloseIcon /></IconButton>
                            </div>
                            {/* <div style={{ marginBottom: '20px' }}>
                                <Typography variant="subtitle2" style={{ marginBottom: '10px', fontWeight: '600' }}>Status</Typography>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <Button fullWidth variant={statusFilter === "All" ? "contained" : "outlined"} onClick={() => setStatusFilter("All")}
                                        style={{ background: statusFilter === "All" ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' : 'transparent', color: statusFilter === "All" ? 'white' : '#0f3460', borderColor: '#0f3460', textTransform: 'none' }}>All</Button>
                                    <Button fullWidth variant={statusFilter === "Completed" ? "contained" : "outlined"} onClick={() => setStatusFilter("Completed")}
                                        style={{ background: statusFilter === "Completed" ? 'linear-gradient(135deg, #10b981, #059669)' : 'transparent', color: statusFilter === "Completed" ? 'white' : '#10b981', borderColor: '#10b981', textTransform: 'none' }}>Completed</Button>
                                    <Button fullWidth variant={statusFilter === "Pending" ? "contained" : "outlined"} onClick={() => setStatusFilter("Pending")}
                                        style={{ background: statusFilter === "Pending" ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'transparent', color: statusFilter === "Pending" ? 'white' : '#f59e0b', borderColor: '#f59e0b', textTransform: 'none' }}>Pending</Button>
                                </div>
                            </div> */}
                            <div>
                                <Typography variant="subtitle2" style={{ marginBottom: '10px', fontWeight: '600' }}>Search</Typography>
                                <TextField fullWidth placeholder="Search deliveries..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} size="small"
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon style={{ color: '#0f3460' }} /></InputAdornment>) }} />
                            </div>
                        </div>
                    </Drawer>

                    {/* Desktop Table View */}
                    {!isMobile && (
                        <div style={{ marginBottom: '10px' }}>
                            <TableContainer
                                component={Paper}
                                style={{
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                    borderRadius: '10px',
                                    overflow: 'hidden'
                                }}
                            >
                                <Table sx={{ minWidth: 650 }}>

                                    <TableHead>
                                        <TableRow style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>

                                            <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', padding: '4px 6px' }}>
                                                Invoice #
                                            </TableCell>

                                            <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', padding: '4px 6px' }}>
                                                Date
                                            </TableCell>

                                            <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', padding: '4px 6px' }}>
                                                Customer
                                            </TableCell>

                                            <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', padding: '4px 6px' }}>
                                                Contact
                                            </TableCell>

                                            <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', padding: '4px 6px', textAlign: 'right' }}>
                                                Cash
                                            </TableCell>

                                            <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', padding: '4px 6px', textAlign: 'right' }}>
                                                UPI
                                            </TableCell>

                                            <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', padding: '4px 6px', textAlign: 'right' }}>
                                                Card
                                            </TableCell>

                                            <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', padding: '4px 6px', textAlign: 'right' }}>
                                                Total
                                            </TableCell>

                                            <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', padding: '4px 6px', textAlign: 'right' }}>
                                                Balance
                                            </TableCell>

                                        </TableRow>
                                    </TableHead>

                                    <TableBody>

                                        {isLoading ? (

                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                                    <CircularProgress size={40} sx={{ color: '#0f3460' }} />
                                                    <Typography sx={{ mt: 2, color: '#64748b' }}>
                                                        Loading Invoices...
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>

                                        ) : groupedData.length === 0 ? (

                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                                    <Typography style={{ fontSize: '12px' }}>
                                                        No Invoice found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>

                                        ) : (

                                            groupedData.map((row, index) => (

                                                <React.Fragment key={index}>

                                                    {/* MAIN INVOICE ROW */}

                                                    <TableRow
                                                        style={{
                                                            background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                                            borderBottom: '2px solid #f0f0f0'
                                                        }}
                                                    >

                                                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', fontWeight: '600' }}>

                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => toggleRow(row.BillNo)}
                                                                    style={{ padding: '2px' }}
                                                                >
                                                                    {expandedRows[row.BillNo]
                                                                        ? <KeyboardArrowUpIcon />
                                                                        : <KeyboardArrowDownIcon />}
                                                                </IconButton>

                                                                {row.BillNo}

                                                            </div>

                                                        </TableCell>

                                                        <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                                                            {formatDate(row.BillDate)}
                                                        </TableCell>

                                                        <TableCell style={{ fontSize: '11px', padding: '8px 12px' }}>
                                                            <strong>{row.CustomerName}</strong>
                                                        </TableCell>

                                                        <TableCell style={{ fontSize: '11px', padding: '8px 12px' }}>
                                                            {row.ContactNumber1}
                                                        </TableCell>

                                                        <TableCell style={{ fontSize: '11px', padding: '8px 12px', textAlign: 'right' }}>
                                                            {row.CashAmount}
                                                        </TableCell>

                                                        <TableCell style={{ fontSize: '11px', padding: '8px 12px', textAlign: 'right' }}>
                                                            {row.UPIAmount}
                                                        </TableCell>

                                                        <TableCell style={{ fontSize: '11px', padding: '8px 12px', textAlign: 'right' }}>
                                                            {row.CardAmount}
                                                        </TableCell>

                                                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                                                            {row.TotalAmount}
                                                        </TableCell>

                                                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                                                            {row.BalanceAmount}
                                                        </TableCell>

                                                    </TableRow>


                                                    {/* CHILD ITEM TABLE */}

                                                    <TableRow>

                                                        <TableCell colSpan={9} style={{ padding: 0 }}>

                                                            <Collapse
                                                                in={expandedRows[row.BillNo]}
                                                                timeout="auto"
                                                                unmountOnExit
                                                            >
                                                                <div
                                                                    style={{
                                                                        margin: '10px',
                                                                        padding: '10px',
                                                                        background: '#f8f9fa',
                                                                        borderRadius: '8px'
                                                                    }}
                                                                >

                                                                    <Table size="small">

                                                                        <TableHead>

                                                                            <TableRow style={{ background: '#e5e7eb' }}>

                                                                                <TableCell style={{ fontWeight: '600', fontSize: '10px' }}>
                                                                                    Item
                                                                                </TableCell>

                                                                                <TableCell align="right" style={{ fontWeight: '600', fontSize: '10px' }}>
                                                                                    Qty
                                                                                </TableCell>

                                                                                <TableCell align="right" style={{ fontWeight: '600', fontSize: '10px' }}>
                                                                                    Rate
                                                                                </TableCell>

                                                                                <TableCell align="right" style={{ fontWeight: '600', fontSize: '10px' }}>
                                                                                    Amount
                                                                                </TableCell>

                                                                                <TableCell align="right" style={{ fontWeight: '600', fontSize: '10px' }}>
                                                                                    VAT
                                                                                </TableCell>

                                                                                {/* <TableCell align="right" style={{ fontWeight: '600', fontSize: '10px' }}>
                                                                                    Total
                                                                                </TableCell> */}

                                                                            </TableRow>

                                                                        </TableHead>


                                                                        <TableBody>

                                                                            {row.items.map((item, i) => (

                                                                                <TableRow key={i}>

                                                                                    <TableCell style={{ fontSize: '11px' }}>
                                                                                        {item.ItemName}
                                                                                    </TableCell>

                                                                                    <TableCell align="right" style={{ fontSize: '11px' }}>
                                                                                        {item.Quantity}
                                                                                    </TableCell>

                                                                                    <TableCell align="right" style={{ fontSize: '11px' }}>
                                                                                        {item.Rate}
                                                                                    </TableCell>

                                                                                    <TableCell align="right" style={{ fontSize: '11px' }}>
                                                                                        {item.Amount}
                                                                                    </TableCell>

                                                                                    <TableCell align="right" style={{ fontSize: '11px' }}>
                                                                                        {item.VATAmount}
                                                                                    </TableCell>

                                                                                    {/* <TableCell align="right"
                                                                                        style={{ fontSize: '11px', fontWeight: '600' }}>
                                                                                        {item.TotalAmount}
                                                                                    </TableCell> */}

                                                                                </TableRow>

                                                                            ))}

                                                                        </TableBody>

                                                                    </Table>

                                                                </div>

                                                            </Collapse>

                                                        </TableCell>

                                                    </TableRow>

                                                </React.Fragment>

                                            ))

                                        )}

                                    </TableBody>

                                </Table>

                            </TableContainer>

                        </div>
                    )}

                    {/* Mobile Card View */}
                    {/* {isMobile && (
                        <div style={{ marginBottom: '10px' }}>
                            {isLoading ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <CircularProgress size={40} sx={{ color: '#0f3460' }} />
                                    <Typography sx={{ mt: 2, color: '#64748b' }}>Loading deliveries...</Typography>
                                </div>
                            ) : processedData.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <Typography color="text.secondary" style={{ fontSize: '12px' }}>No Invoice found</Typography>
                                </div>
                            ) : (
                                processedData.map((row, index) => (
                                    <div key={row.BillNo + index} style={{
                                        background: 'white',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        marginBottom: '10px',
                                        padding: '12px',
                                        border: '1px solid #e5e7eb'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <IconButton size="small" onClick={() => toggleRow(row.BillNo)} style={{ padding: '2px' }}>
                                                    {expandedRows[row.BillNo] ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                                </IconButton>
                                                <span style={{ fontWeight: '700', fontSize: '13px', color: '#0f3460' }}>{row.BillNo}</span>
                                            </div>
                                            {row.Status === "Delivery In Completed" ? (
                                                <Chip icon={<CheckCircleIcon style={{ color: '#ffffff', fontSize: '12px' }} />} label="COMPLETED"
                                                    sx={{
                                                        background: 'linear-gradient(145deg, #10b981, #059669)', color: '#ffffff', fontWeight: '700', fontSize: '8px', height: '22px', borderRadius: '15px',
                                                        '& .MuiChip-label': { padding: '0 8px', color: '#ffffff' }
                                                    }} />
                                            ) : (
                                                <Chip icon={<PendingIcon style={{ color: '#ffffff', fontSize: '12px' }} />} label="PENDING"
                                                    sx={{
                                                        background: 'linear-gradient(145deg, #f59e0b, #d97706)', color: '#ffffff', fontWeight: '700', fontSize: '8px', height: '22px', borderRadius: '15px',
                                                        '& .MuiChip-label': { padding: '0 8px', color: '#ffffff' }
                                                    }} />
                                            )}
                                        </div>

                                        <div style={{ fontSize: '10px', color: '#64748b', lineHeight: '1.6' }}>
                                            <div><strong>Date:</strong> {formatDate(row.BillDate)} | <strong>Customer:</strong> {row.CustomerName}</div>
                                            <div><strong>Event:</strong> {row.EventName} | <strong>Program:</strong> {row.ProgramDate}</div>
                                            <div><strong>Expected Time:</strong> {row.ExpectedTime} | <strong>Days:</strong> E:{row.EventDays} R:{row.RentDays}</div>
                                            <div><strong>Out:</strong> {row.DeliveryOutVehicleNo} @ {row.DeliveryOutTime} | <strong>In:</strong> {row.DeliveryInVehicleNo} @ {row.DeliveryInTime}</div>
                                        </div>

                                        <Collapse in={expandedRows[row.BillNo]} timeout="auto" unmountOnExit>
                                            <div style={{ marginTop: '10px', padding: '8px', background: '#f8f9fa', borderRadius: '6px' }}>
                                                <Typography variant="subtitle2" style={{ fontWeight: '600', fontSize: '11px', marginBottom: '6px' }}>Items</Typography>
                                                {row.items && row.items.map((item, itemIndex) => (
                                                    <div key={itemIndex} style={{
                                                        fontSize: '10px',
                                                        padding: '8px',
                                                        background: 'white',
                                                        borderRadius: '6px',
                                                        marginBottom: '6px',
                                                        border: '1px solid #e5e7eb'
                                                    }}>
                                                        <div style={{ fontWeight: '600', marginBottom: '6px', color: '#2d3748' }}>{item.ItemName}</div>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', fontSize: '9px' }}>
                                                            <span style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>Out: {item.DeliveryOutQuantity}</span>
                                                            <span style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>Good: {item.GoodQuantity}</span>
                                                            {item.DamagedQuantity > 0 && (
                                                                <span style={{
                                                                    color: 'white',
                                                                    background: 'red',
                                                                    borderRadius: '8px',
                                                                    padding: '2px 6px',
                                                                    fontWeight: '600'
                                                                }}>Dmg: {item.DamagedQuantity}</span>
                                                            )}
                                                            {item.DeliveryInQuantity > 0 && (
                                                                <span style={{
                                                                    color: 'white',
                                                                    background: item.DeliveryInQuantity === item.GoodQuantity ? 'green' : '#f59e0b',
                                                                    borderRadius: '8px',
                                                                    padding: '2px 6px',
                                                                    fontWeight: '600'
                                                                }}>In: {item.DeliveryInQuantity}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </Collapse>
                                    </div>
                                ))
                            )}
                        </div>
                    )} */}

                    <TablePagination component="div" count={filteredData.length} page={page} onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-toolbar': { fontSize: '12px' }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '12px', fontWeight: 600 } }} />
                </div>
            </div>
        </Fade>
    );
}

export default DetailedInvoiceReport;
